import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Stack, Text, RadioButton, Overlay, LoadingBalls } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { US_RETURN_OPTIONS_FEDEX, INTERNATIONAL_RETURN_OPTIONS } from '../../../constants';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useOrder } from '../../../hooks/useOrder';
import { useReturnSummary, useUpdateReturnSummaryMutation } from '../../../hooks/useReturnSummary';

import './RequestReturnOptions.scss';

export const RequestReturnOptions: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { showErrorModal } = useErrorModal();
  const { order } = useOrder(orderNumber);
  const { returnSummary } = useReturnSummary({ orderNumber });
  const [updateReturnSummary, { loading }] = useUpdateReturnSummaryMutation(orderNumber);

  const handleChange = useCallback(
    async (returnOptionId: number) => {
      if (returnSummary?.attributes.returnOptionId === returnOptionId) return;

      try {
        await updateReturnSummary({ returnOptionId });
      } catch (error) {
        reportError('REQUEST_RETURN_CHANGE_OPTION', error);
        showErrorModal();
      }
    },
    [updateReturnSummary, showErrorModal, returnSummary]
  );

  if (!returnSummary || !order) return null;

  const options =
    order.attributes.address.countryCode === 'US'
      ? US_RETURN_OPTIONS_FEDEX
      : INTERNATIONAL_RETURN_OPTIONS;

  return (
    <div className="RequestReturnOptions">
      <Stack space={4}>
        <Text treatment="h5">Return Options</Text>
        {options.map(option => (
          <Stack key={option.value} space={1}>
            <RadioButton
              checked={returnSummary.attributes.returnOptionId === option.value}
              onChange={() => handleChange(option.value)}
            >
              {option.label}
            </RadioButton>
            {returnSummary.attributes.returnOptionId === option.value &&
              option.description.map(text => (
                <Text key={text} className="RequestReturnOptions__description">
                  {text}
                </Text>
              ))}
          </Stack>
        ))}
      </Stack>

      <Overlay show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Changing</Text>
      </Overlay>
    </div>
  );
};
